import { applyQueueAssign, applyQueueMove } from '@chiroup/core/functions/connectorQueueCache';
import {
  ConnectorVendorStatus,
  QueueDisposition,
  UnroutedExchangeSummary,
  UnroutedQueueList,
} from '@chiroup/core/types/ConnectorAdmin.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import connectionApiService, {
  ConnectionApiConsole,
  UnroutedDispositionChangeResult,
} from '../services/connectionApi.service';

/**
 * Connection API hooks — ONE domain hook per consuming concern (the
 * `useCustomForms` house shape), never one hook per endpoint. Each hook owns
 * a query cache (keys stay module-private) and returns the query plus the
 * mutations allowed to touch it; mutations patch the cache from their own
 * responses wherever the response carries what changed, and invalidate only
 * when the effects genuinely span the console.
 */

const KEY_BASE = ['connection-api'] as const;
/** ONE query paints the console. */
const CONSOLE_KEY = [...KEY_BASE, 'console'] as const;

/**
 * State-changing ops whose effects genuinely span the console (credential
 * checks rewrite many keys + clinic rows; drain/transmit kicks move errors and
 * activity) re-read the one console query. Everything narrower patches the
 * cache from its own response instead.
 */
const useConsoleMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: () => queryClient.invalidateQueries(CONSOLE_KEY),
  });
};

/** The console page: the one read that paints it, plus its page-level actions. */
export const useConnectionApiConsole = (
  includeReviewed = false,
  /**
   * A kicked manual run re-reads the console on this interval until its
   * durable runId reaches completed/failed. Off when no requested run is active.
   */
  refetchInterval: number | false = false
) => {
  const queryClient = useQueryClient();
  const query = useQuery(
    [...CONSOLE_KEY, includeReviewed],
    () => connectionApiService.consoleStatus(includeReviewed),
    { refetchOnWindowFocus: false, retry: false, refetchInterval }
  );

  /**
   * Check health = GET /health (the tiny liveness probe), never a console
   * re-read. The fresh ok/stage/version patch the cached service block;
   * `execution` isn't in the probe's payload, so the cached value survives.
   */
  const checkHealth = useMutation(() => connectionApiService.health(), {
    onSuccess: data => {
      queryClient.setQueriesData<ConnectionApiConsole | undefined>(
        { queryKey: CONSOLE_KEY },
        prev => (prev ? { ...prev, service: { ...prev.service, ...data } } : prev)
      );
    },
  });

  return { ...query, checkHealth };
};

/** One vendor row's run-now kicks — all world-changing, all re-read the console. */
export const useVendorActions = () => ({
  runCredentialCheck: useConsoleMutation((vendor?: string) =>
    connectionApiService.runCredentialCheck(vendor)
  ),
  runTransmitBackstop: useConsoleMutation((vendor: string) =>
    connectionApiService.transmitRun(vendor)
  ),
  /** Per-vendor "run inbound drain now" — the console's watch window shows what lands. */
  runInboundDrain: useConsoleMutation((vendor: string) =>
    connectionApiService.inboundDrainRun(vendor)
  ),
  /** Turn automation polling on/off for the caller's own combo. */
  togglePolling: useConsoleMutation((args: { vendor: string; enabled: boolean }) =>
    connectionApiService.togglePolling(args.vendor, args.enabled)
  ),
});

/**
 * Key mutations change ~50 bytes of ONE vendor's block; their responses carry
 * exactly what changed. Patch the cached console instead of re-buying the
 * whole read (the queue caches' rule, applied here).
 */
const useVendorPatchMutation = <TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  vendorOf: (args: TArgs) => string,
  apply: (vendor: ConnectorVendorStatus, data: TResult, args: TArgs) => ConnectorVendorStatus
) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: (data, args) => {
      queryClient.setQueriesData<ConnectionApiConsole | undefined>(
        { queryKey: CONSOLE_KEY },
        prev =>
          prev
            ? {
                ...prev,
                vendors: prev.vendors.map(v =>
                  v.vendor === vendorOf(args) ? apply(v, data, args) : v
                ),
              }
            : prev
      );
    },
  });
};

/** The vendor keys panel: create/validate/route/deprecate/delete, all cache patches. */
export const useConnectorKeys = () => ({
  createKey: useVendorPatchMutation(
    ({
      vendor,
      integrationId,
      keyName,
      apiKey,
    }: {
      vendor: string;
      integrationId: number;
      keyName: string;
      apiKey: string;
    }) => connectionApiService.createKey(vendor, integrationId, keyName, apiKey),
    args => args.vendor,
    (v, data) => ({ ...v, keys: { ...v.keys, [data.keyName]: { metadata: {}, audit: data.audit } } })
  ),

  validateKey: useVendorPatchMutation(
    ({
      vendor,
      integrationId,
      keyName,
    }: {
      vendor: string;
      integrationId: number;
      keyName: string;
    }) => connectionApiService.validateKey(vendor, integrationId, keyName),
    args => args.vendor,
    (v, data) => ({
      ...v,
      keys: {
        ...v.keys,
        [data.keyName]: {
          ...v.keys[data.keyName],
          audit: data.audit,
          metadata: data.metadata ?? v.keys[data.keyName]?.metadata ?? {},
        },
      },
    })
  ),

  setKeyRouting: useVendorPatchMutation(
    ({
      vendor,
      routing,
    }: {
      vendor: string;
      routing: Parameters<typeof connectionApiService.setKeyRouting>[1];
    }) => connectionApiService.setKeyRouting(vendor, routing),
    args => args.vendor,
    (v, data) => ({ ...v, keyRouting: data.keyRouting })
  ),

  setKeyMetadata: useVendorPatchMutation(
    ({
      vendor,
      keyName,
      patch,
    }: {
      vendor: string;
      keyName: string;
      patch: Parameters<typeof connectionApiService.setKeyMetadata>[2];
    }) => connectionApiService.setKeyMetadata(vendor, keyName, patch),
    args => args.vendor,
    (v, data) => ({
      ...v,
      keys: { ...v.keys, [data.keyName]: { ...v.keys[data.keyName], metadata: data.metadata } },
    })
  ),

  deleteKey: useVendorPatchMutation(
    ({ vendor, keyName }: { vendor: string; keyName: string }) =>
      connectionApiService.deleteKey(vendor, keyName),
    args => args.vendor,
    (v, data) => {
      const keys = { ...v.keys };

      delete keys[data.keyName];

      return { ...v, keys };
    }
  ),
});

// ── Operations tab (INFRA-11): reads sized to the click, none on the console.
const OPERATIONS_KEY = [...KEY_BASE, 'operations'] as const;
const OPERATION_RUNS_KEY = [...KEY_BASE, 'operation-runs'] as const;

/**
 * The Operations page: the bare list on mount, ONE operation's schema when
 * selected, run history only when asked for. The hook owns the derivations
 * (effective vendor/operation from the selections + list); the component owns
 * only its UI state.
 */
export const useConnectorOperations = ({
  selectedVendor,
  selectedOperation,
  runsRequested,
}: {
  selectedVendor: string | null;
  selectedOperation: string | null;
  runsRequested: boolean;
}) => {
  const queryClient = useQueryClient();

  /** The bare per-vendor operation list — fetched when the tab opens. */
  const list = useQuery(OPERATIONS_KEY, () => connectionApiService.listOperations(), {
    refetchOnWindowFocus: false,
    // The catalog changes only on deploy.
    staleTime: Infinity,
  });

  const operationsByVendor = list.data?.operations ?? {};
  const vendorNames = Object.keys(operationsByVendor).filter(
    v => (operationsByVendor[v] ?? []).length
  );
  const vendor = selectedVendor ?? vendorNames[0] ?? null;
  const catalog = vendor ? (operationsByVendor[vendor] ?? []) : [];
  const operationName = selectedOperation ?? catalog[0]?.name ?? null;

  /** ONE operation's field schema — fetched when it is selected. */
  const detail = useQuery(
    [...OPERATIONS_KEY, vendor, operationName],
    () => connectionApiService.getOperation(vendor ?? '', operationName ?? ''),
    { enabled: !!(vendor && operationName), refetchOnWindowFocus: false, staleTime: Infinity }
  );
  const operation = detail.data?.operation.name === operationName ? detail.data.operation : null;

  /** Run history — fetched only when the user asks for it. */
  const runs = useQuery(OPERATION_RUNS_KEY, () => connectionApiService.listOperationRuns(), {
    enabled: runsRequested,
    refetchOnWindowFocus: false,
  });

  const runOperation = useMutation(
    ({
      vendor: runVendor,
      operation: runOp,
      input,
      confirm,
    }: {
      vendor: string;
      operation: string;
      input: Record<string, unknown>;
      confirm?: boolean;
    }) => connectionApiService.runOperation(runVendor, runOp, input, confirm),
    {
      // A run adds ONE durable row; only the (small, on-demand) runs read is
      // stale — never the console.
      onSettled: () => queryClient.invalidateQueries(OPERATION_RUNS_KEY),
    }
  );

  /** Prefill is a pure read — nothing to invalidate. */
  const prefill = useMutation(
    ({
      vendor: prefillVendor,
      operation: prefillOp,
      input,
    }: {
      vendor: string;
      operation: string;
      input: Record<string, unknown>;
    }) => connectionApiService.prefillOperation(prefillVendor, prefillOp, input)
  );

  return {
    vendorNames,
    vendor,
    catalog,
    operationName,
    operation,
    isCatalogLoading: list.isLoading || detail.isLoading,
    runs,
    runOperation,
    prefill,
  };
};

const UNROUTED_KEY = [...KEY_BASE, 'unrouted'] as const;

/**
 * Assign REMOVES the row — it leaves the queue for a clinic. Patch, don't
 * refetch: drop it from the cache that holds it (each cache echoes its
 * `disposition`) and decrement the unrouted total in EVERY cached queue copy
 * (all three responses carry all three totals).
 */
const useUnroutedRemoval = <TArgs extends { exchangeId: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: (_data, args) => {
      queryClient.setQueriesData<UnroutedQueueList | undefined>({ queryKey: UNROUTED_KEY }, prev =>
        prev ? applyQueueAssign(prev, args.exchangeId) : prev
      );
    },
    // A refusal persists a new `meta.unrouted[0]` diagnosis before returning 400.
    // Re-read it so the diagnosis remains visible after the mutation error is reset.
    onError: () => queryClient.invalidateQueries(UNROUTED_KEY),
  });
};

/**
 * Close/reopen MOVE the row between queues (Unrouted/Unsupported ⇄ Resolved).
 * With one cache per disposition (item 4), the patch is a move: drop the row
 * from the source cache, insert the patched copy into the target cache when
 * that queue has been loaded, and shift both totals in every cached copy —
 * still no invalidate-and-refetch. The patched row is built from the CACHED
 * copy (the response doesn't repeat display fields) + the response's
 * disposition/reprocessable/change:
 * - history prepends newest first, matching the server's prepend — appending
 *   here left the trail in the opposite order until the next refetch.
 * - `reprocessable` is patched too: it is computed per disposition, and
 *   carrying the old value left a reopened Unsupported row's Reprocess button
 *   stale until a full reload.
 */
const useUnroutedDispositionChange = <TArgs extends { exchangeId: string }>(
  fn: (args: TArgs) => Promise<UnroutedDispositionChangeResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<UnroutedDispositionChangeResult, unknown, TArgs>(fn, {
    onSuccess: data => {
      const caches = queryClient.getQueriesData<UnroutedQueueList | undefined>({
        queryKey: UNROUTED_KEY,
      });
      const source = caches
        .flatMap(([, cached]) => cached?.rows ?? [])
        .find(r => r.exchangeId === data.exchangeId);

      // The acted-on row should always be in a cache (the user clicked it); if
      // it is not, a full re-read is the only honest recovery.
      if (!source || source.disposition === data.disposition) {
        queryClient.invalidateQueries(UNROUTED_KEY);

        return;
      }

      const from = source.disposition as QueueDisposition;
      const to = data.disposition as QueueDisposition;
      const patched: UnroutedExchangeSummary = {
        ...source,
        disposition: data.disposition,
        reprocessable: data.reprocessable,
        history: [data.change, ...source.history],
      };

      queryClient.setQueriesData<UnroutedQueueList | undefined>({ queryKey: UNROUTED_KEY }, prev =>
        prev ? applyQueueMove(prev, { from, to, patched }) : prev
      );
    },
  });
};

/**
 * The Queues page: one query per disposition (item 4: only the visible queue
 * is fetched) plus the row actions that move things through it.
 */
export const useUnroutedQueue = (disposition: QueueDisposition) => {
  const queryClient = useQueryClient();
  const query = useQuery(
    [...UNROUTED_KEY, disposition],
    () => connectionApiService.listUnrouted(disposition),
    { refetchOnWindowFocus: false }
  );

  const assign = useUnroutedRemoval(
    ({ exchangeId, clinicId }: { exchangeId: string; clinicId: number }) =>
      connectionApiService.assignUnrouted(exchangeId, clinicId)
  );

  const close = useUnroutedDispositionChange(
    ({ exchangeId, reason }: { exchangeId: string; reason: string }) =>
      connectionApiService.closeUnrouted(exchangeId, reason)
  );

  const reopen = useUnroutedDispositionChange(
    ({ exchangeId, message }: { exchangeId: string; message: string }) =>
      connectionApiService.reopenUnrouted(exchangeId, message)
  );

  /**
   * Reprocess ONE Unsupported row. The row LEAVES the Unsupported view either
   * way — it becomes Processed under a clinic, or Unrouted with routing
   * candidates it did not have before — so the queue is invalidated rather
   * than patched: the new shape comes from the funnel, not from this response.
   */
  const reprocess = useMutation(
    (args: { exchangeId: string }) => connectionApiService.reprocessExchange(args.exchangeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(UNROUTED_KEY);
      },
    }
  );

  return { ...query, assign, close, reopen, reprocess };
};

/** The error-detail modal: ONE exchange by id (only while open) + its row actions. */
export const useActivityDetail = (exchangeId: string | null) => ({
  exchange: useQuery(
    [...KEY_BASE, 'exchange', exchangeId],
    () => connectionApiService.fetchExchange(exchangeId as string),
    { enabled: !!exchangeId, refetchOnWindowFocus: false, retry: false }
  ),
  /** Re-drive ONE exchange (inbound re-dispatch or outbound re-enqueue); refreshes the console. */
  retry: useConsoleMutation((args: { exchangeId: string; trace?: boolean }) =>
    connectionApiService.retryExchange(args.exchangeId, args.trace ?? false)
  ),
  /** Mark an activity row reviewed (hide it) or restore it; refreshes the console. */
  review: useConsoleMutation((args: { id: number; reviewed: boolean }) =>
    connectionApiService.reviewActivity(args.id, args.reviewed)
  ),
});

/** The transmit panel's controls. */
export const useTransmitControls = () => ({
  clearLock: useConsoleMutation((vendor: string) => connectionApiService.clearTransmitLock(vendor)),
  /** Empty the transmit FIFO (ghost cleanup) — Pending rows survive and re-enqueue on the next run. */
  purgeQueue: useConsoleMutation(() => connectionApiService.purgeTransmitQueue()),
});

/** One vendor's trace panel: start/stop a session + the natural-expiry re-read. */
export const useTraceSession = (vendor?: string, integrationId?: number) => {
  const queryClient = useQueryClient();

  const setConfig = useMutation(
    (body: Parameters<typeof connectionApiService.setTraceConfig>[2]) =>
      connectionApiService.setTraceConfig(vendor!, integrationId!, body),
    {
      // The response IS the fresh config — patch it into the console payload
      // rather than invalidate-and-refetch. setQueriesData (PREFIX match), not
      // setQueryData (exact): the live console key carries the `includeReviewed`
      // flag (`[...CONSOLE_KEY, bool]`), so an exact CONSOLE_KEY write would
      // miss it and the session would never flip to active.
      onSuccess: data => {
        queryClient.setQueriesData<ConnectionApiConsole | undefined>(
          { queryKey: CONSOLE_KEY },
          prev =>
            prev && vendor
              ? { ...prev, traceConfigs: { ...prev.traceConfigs, [vendor]: data } }
              : prev
        );
      },
    }
  );

  return {
    setConfig,
    /**
     * Re-read the console outside a mutation — e.g. the natural-expiry
     * timeout flipping the session label off (the key stays module-private).
     */
    invalidateConsole: () => queryClient.invalidateQueries(CONSOLE_KEY),
  };
};
